import { REPORT_WINDOW_MAX_DAYS } from '@fieldvision/contracts';
import { Lock, X } from 'lucide-react';
import type { ReactNode } from 'react';

import { useAllClassifications, useChannels, useRegions } from '../../lib/reference.js';
import { regionLabel, toDateInput } from '../../lib/format.js';
import {
  pruneClassifications,
  useReportFilters,
  windowError,
} from '../../lib/report-filters.store.js';
import {
  closedMonthsInWindow,
  monthLabel,
  useClosedPeriods,
} from '../../lib/use-closed-periods.js';
import { Button } from './Button.js';
import { FilterPanel } from './FilterPanel.js';
import { selectionSummary } from './multi-select.logic.js';
import { MultiSelect } from './MultiSelect.js';
import { OutletMultiPicker, UserMultiPicker } from './ReferenceMultiPicker.js';
import { Banner, Field, Input } from './primitives.js';

/**
 * The report builder — PWA-08.
 *
 * `from` and `to` are required and the window is capped at 90 days, both
 * enforced by the API with a 400. They are enforced here too, and that is not
 * belt-and-braces: a rejected request renders as a failed query, which looks
 * identical to the server being down. The manager needs to know they asked for
 * 120 days, in the place where they asked.
 *
 * Presets exist because "last 30 days" is the overwhelmingly common intent and
 * two date pickers is a poor way to express it.
 *
 * TWO LAYOUTS, ONE STORE.
 * `/reports` renders every dimension in a grid; `/overview` renders the dates
 * plus region and channel in a single row — one column on a phone, where "row"
 * is a promise no 360px viewport can keep. Both bind the SAME store, which is
 * the point — a dashboard number and the report it links to must be over the
 * same window, or the deep link lies.
 *
 * A dimension that is bound but not rendered (an `outletId` left over from a
 * visit to `/reports`) is still applied to every query, so it is shown as a
 * dismissible chip rather than silently narrowing the dashboard.
 *
 * THREE ESCAPE HATCHES, all added for `/price-index` and all deliberately narrow.
 * `maxDays={null}` drops the 90-day warning on the one screen whose endpoint has
 * no cap — see `windowError`. `children` lets a page add its own filters INTO
 * this panel rather than stacking a second one below it: two filter panels on one
 * screen read as two independent filter sets, and a manager reasonably concludes
 * the second one does not apply to what the first one selected.
 *
 * `onReset` is the other half of `children`, and passing the first without the
 * second is a bug. "Atur ulang filter" clears the STORE, which knows nothing
 * about a filter a page holds in its own state — so the SKU picker went on
 * narrowing the table under a panel that had just announced it was cleared. Any
 * page that passes `children` must clear them here.
 */
const PRESETS: Array<{ label: string; days: number }> = [
  { label: '7 hari', days: 7 },
  { label: '30 hari', days: 30 },
  { label: '90 hari', days: REPORT_WINDOW_MAX_DAYS },
];

export type FilterField = 'region' | 'channel' | 'classification' | 'outlet' | 'salesUser';

const ALL_FIELDS: FilterField[] = ['region', 'channel', 'classification', 'outlet', 'salesUser'];

export function FilterBar({
  fields = ALL_FIELDS,
  layout = 'grid',
  maxDays = REPORT_WINDOW_MAX_DAYS,
  children,
  onReset,
}: {
  fields?: FilterField[];
  layout?: 'grid' | 'row';
  /** `null` on the endpoints with no window ceiling. See `windowError`. */
  maxDays?: number | null;
  /** Page-specific filters, rendered alongside the shared dimensions. */
  children?: ReactNode;
  /** Clears whatever `children` holds. Required whenever `children` is passed. */
  onReset?: () => void;
}) {
  const filters = useReportFilters();
  const shows = (field: FilterField) => fields.includes(field);

  const regions = useRegions();
  const regionOptions = (regions.data ?? []).map((region) => ({
    id: region.id,
    label: regionLabel(region),
  }));
  const channels = useChannels();
  const channelOptions = (channels.data ?? []).map((channel) => ({
    id: channel.id,
    label: channel.name,
  }));
  /*
   * Only fetched once a channel is chosen, and only the chosen channels' offered
   * — see `useClassifications`. With two or more channels chosen each option
   * carries its channel, since "Reguler" alone would appear once per channel
   * with nothing to tell them apart.
   */
  const allClassifications = useAllClassifications(filters.channelIds.length > 0);
  const chosenChannels = new Set(filters.channelIds);
  const classificationOptions = (allClassifications.data ?? [])
    .filter((row) => chosenChannels.has(row.channelId))
    .map((row) => ({
      id: row.id,
      label: filters.channelIds.length > 1 ? `${row.name} (${row.channelName})` : row.name,
    }));
  const setChannels = (channelIds: string[]) =>
    filters.set({
      channelIds,
      classificationIds: pruneClassifications(
        allClassifications.data ?? [],
        channelIds,
        filters.classificationIds,
      ),
    });

  const error = windowError(filters, maxDays);

  /*
   * WHICH MONTHS IN THIS RANGE ARE FINAL — migration 0047.
   *
   * The console keeps a free date range rather than switching to a month picker,
   * so a range routinely covers a locked month and a live one at once. Without
   * this line a manager has no way to tell which half of a number can still move
   * — and the whole point of the lock is that some of it cannot.
   */
  const periods = useClosedPeriods();
  const lockedMonths = closedMonthsInWindow(periods.data?.periods, filters.from, filters.to);

  const applyPreset = (days: number) => {
    filters.set({
      from: toDateInput(new Date(Date.now() - days * 86_400_000)),
      to: toDateInput(new Date()),
    });
  };

  const hiddenActive: Array<{ field: FilterField; label: string; clear: () => void }> = [];
  if (!shows('outlet') && filters.outlets.length > 0) {
    hiddenActive.push({
      field: 'outlet',
      // The stored labels, so this reads "Outlet: Indomaret Cikarang" rather
      // than the "dipilih" it used to fall back to whenever the picker's own
      // query was disabled — which, on a layout that does not render it, was always.
      label: pickedChip('Outlet', filters.outlets),
      clear: () => filters.set({ outlets: [] }),
    });
  }
  if (!shows('salesUser') && filters.salesUsers.length > 0) {
    hiddenActive.push({
      field: 'salesUser',
      label: pickedChip('Petugas', filters.salesUsers),
      clear: () => filters.set({ salesUsers: [] }),
    });
  }
  if (!shows('region') && filters.regionIds.length > 0) {
    const summary = selectionSummary(regionOptions, filters.regionIds, '', 'Distributor');
    hiddenActive.push({
      field: 'region',
      label: filters.regionIds.length === 1 ? `Distributor: ${summary}` : summary,
      clear: () => filters.set({ regionIds: [] }),
    });
  }
  if (!shows('channel') && filters.channelIds.length > 0) {
    // Resolved to a name. The chip exists to tell someone their dashboard is
    // narrowed and by what — "Channel: 8f3a…" tells them the first half only.
    const summary = selectionSummary(channelOptions, filters.channelIds, '', 'Channel');
    hiddenActive.push({
      field: 'channel',
      label: filters.channelIds.length === 1 ? `Channel: ${summary}` : summary,
      clear: () => filters.set({ channelIds: [] }),
    });
  }
  if (!shows('classification') && filters.classificationIds.length > 0) {
    const summary = selectionSummary(
      classificationOptions,
      filters.classificationIds,
      '',
      'Klasifikasi',
    );
    hiddenActive.push({
      field: 'classification',
      label: filters.classificationIds.length === 1 ? `Klasifikasi: ${summary}` : summary,
      clear: () => filters.set({ classificationIds: [] }),
    });
  }

  const selects = (
    <>
      {shows('region') ? (
        // SEVERAL AT ONCE — "Jakarta Barat and Bogor" is one question a manager
        // asks, and a single-value select made it two reports read side by side.
        <Field label="Distributor">
          <MultiSelect
            options={regionOptions}
            value={filters.regionIds}
            onChange={(regionIds) => filters.set({ regionIds })}
            allLabel="Semua Distributor"
            noun="Distributor"
          />
        </Field>
      ) : null}

      {shows('channel') ? (
        // Several at once, like Distributor above.
        <Field label="Channel">
          <MultiSelect
            options={channelOptions}
            value={filters.channelIds}
            onChange={setChannels}
            allLabel="Semua Channel"
            noun="Channel"
          />
        </Field>
      ) : null}

      {shows('classification') ? (
        /*
         * DISABLED UNTIL A CHANNEL IS CHOSEN, rather than listing every
         * classification in the system. A classification belongs to exactly one
         * channel and its name is only unique within that channel, so an
         * unscoped list shows the same word several times over with no way to
         * tell which is which — a picker that cannot be used correctly.
         *
         * Unticking a channel drops its classifications (`setChannels`), and
         * unticking the last one clears them all (the store), so a stale value
         * cannot survive into a filter the picker no longer shows.
         */
        <Field
          label="Klasifikasi"
          hint={filters.channelIds.length > 0 ? undefined : 'Pilih Channel dahulu'}
        >
          <MultiSelect
            options={classificationOptions}
            value={filters.classificationIds}
            onChange={(classificationIds) => filters.set({ classificationIds })}
            allLabel="Semua klasifikasi"
            noun="Klasifikasi"
            disabled={filters.channelIds.length === 0}
          />
        </Field>
      ) : null}

      {shows('outlet') ? (
        /*
         * REGION NARROWING MOVED TO THE SERVER. This used to filter the option
         * array in the browser, which was tolerable over a list claiming to be
         * every outlet and is wrong over a page of twenty: filtering an already
         * narrowed page hides outlets that do match.
         *
         * ONLY WHEN EXACTLY ONE DISTRIBUTOR IS CHOSEN. `/masterdata/outlets`
         * narrows by one region, so with two or more the picker lists every
         * outlet — an outlet from outside them simply returns an empty report,
         * the same outcome the Petugas picker has always had with any region.
         */
        <Field label="Outlet">
          <OutletMultiPicker
            value={filters.outlets}
            regionId={filters.regionIds.length === 1 ? filters.regionIds[0] : undefined}
            onChange={(outlets) => filters.set({ outlets })}
            allLabel="Semua Outlet"
          />
        </Field>
      ) : null}

      {shows('salesUser') ? (
        <Field label="Petugas">
          <UserMultiPicker
            value={filters.salesUsers}
            onChange={(salesUsers) => filters.set({ salesUsers })}
            allLabel="Semua petugas"
          />
        </Field>
      ) : null}

      {children}
    </>
  );

  const dates = (
    <>
      <div className={layout === 'row' ? 'w-full sm:w-44' : 'w-full sm:w-auto'}>
        <Field label="Dari">
          <Input
            type="date"
            value={filters.from}
            max={filters.to}
            onChange={(event) => filters.set({ from: event.target.value })}
          />
        </Field>
      </div>
      <div className={layout === 'row' ? 'w-full sm:w-44' : 'w-full sm:w-auto'}>
        <Field label="Sampai">
          <Input
            type="date"
            value={filters.to}
            min={filters.from}
            max={toDateInput(new Date())}
            onChange={(event) => filters.set({ to: event.target.value })}
          />
        </Field>
      </div>

      {/*
        * FULL-WIDTH THIRDS ON MOBILE, shrink-to-fit from `sm`. The presets are
        * the same choice repeated three times, so on one column they should
        * read as one control the width of the date fields above them rather
        * than as three buttons huddled against the left edge. `grid-cols-3`
        * rather than `flex-1` on each: equal columns regardless of label width,
        * which `Button`'s own `shrink-0` would otherwise fight.
        */}
      <div className="grid w-full grid-cols-3 gap-2 pb-0.5 sm:flex sm:w-auto">
        {PRESETS.map((preset) => (
          <Button key={preset.days} size="sm" onClick={() => applyPreset(preset.days)}>
            {preset.label}
          </Button>
        ))}
      </div>
    </>
  );

  return (
    <FilterPanel layout="none">
      {layout === 'row' ? (
        <div className="flex flex-wrap items-end gap-3">
          {dates}
          {/*
            * `w-full` BEFORE `sm:flex-1`, and the order matters. `flex-1` alone
            * is `flex-basis: 0`, so on one column this block did not wrap onto
            * its own line — it took whatever was left beside the presets and
            * rendered Distributor and Channel as two ~90px selects reading
            * "Semua". A filter you cannot read the value of is not a filter.
            */}
          <div className="grid w-full min-w-0 gap-3 sm:w-auto sm:flex-1 sm:grid-cols-2">{selects}</div>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-3">{dates}</div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">{selects}</div>
        </>
      )}

      {hiddenActive.length > 0 ? (
        <ul className="flex flex-wrap gap-2">
          {hiddenActive.map((chip) => (
            <li key={chip.field}>
              <button
                type="button"
                onClick={chip.clear}
                className="focus-ring inline-flex items-center gap-1.5 rounded-full bg-tint px-3 py-1 text-sm font-semibold text-primary-700 hover:bg-panel"
              >
                {chip.label}
                <X size={14} aria-hidden />
                <span className="sr-only">Hapus filter</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {error ? <Banner tone="warn">{error}</Banner> : null}

      {/*
        * INFO, NOT WARN. Nothing is wrong here and nothing needs fixing — the
        * figures for these months are simply settled. A warn tone beside a
        * genuine range error would train people to read both as problems, and
        * the range error is the one that stops a request.
        */}
      {lockedMonths.length > 0 ? (
        <Banner tone="info">
          <Lock size={14} className="mr-1.5 inline align-[-2px]" aria-hidden />
          {lockedMonths.length === 1
            ? `${monthLabel(lockedMonths[0] ?? '')} sudah terkunci — angkanya final dan tidak berubah lagi.`
            : `${lockedMonths.map(monthLabel).join(', ')} sudah terkunci — angkanya final dan tidak berubah lagi.`}
        </Banner>
      ) : null}

      <div className="flex justify-end">
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            filters.reset();
            onReset?.();
          }}
        >
          Atur ulang filter
        </Button>
      </div>
    </FilterPanel>
  );
}

/**
 * A chip for picked outlets or petugas the current layout does not render — the
 * one name when there is one, a count otherwise, the wording `selectionSummary`
 * uses for the pickers themselves.
 */
function pickedChip(noun: string, picked: ReadonlyArray<{ label: string }>): string {
  return picked.length === 1 ? `${noun}: ${picked[0]?.label || 'dipilih'}` : `${picked.length} ${noun} dipilih`;
}
